import { getWSService } from "./WebSocketCli";
import { WebSocketMessage } from "../../model/WebSocketMessage";
import { broadcastScene } from "../scenePollLeader";
import { calculateAgeForTest, parseDateOnly } from "../CommonUtil";
import { ITestAlgoConfig, TestAlgoConfig } from "../../model/TestAlgoConfig";
import { getOrganizationId } from "../AuthUtil";
import {
  updateProductBatch,
  updateStatus,
  updateVolume,
  updateBattery,
  clearTestProgress,
  updateCurrentVersion,
  updateScene,
  updateControllers,
  updateControllerBattery,
  updateDeviceStatus,
} from "@actions/productActions";
import { statusFromScene } from "../../hooks/useScenePolling";
import { clearHeadsetRebootPending } from "../headsetRebootPending";
import {
  setCalibrationLifecycle,
  setIsCurrentlyCalibrating,
  setIsGazeOffBounds,
  setTestCompletedPercentage,
  setTestStatus,
  sendTestTerminateMessage,
  updateCollectedTestData,
  updateGazeData,
  updatePupilSizeData,
  setCurrentVirtualAssistanceVideo,
  setVirtualAssistanceStatus,
  resumeTest,
  updateTimeScale,
  setIsSceneReady,
  setTestPhase,
  setHeadsetActiveTest,
  startTestingNextEyeInBackToBackTest,
  setIsLastBackToBackEyeTest,
  type VirtualAssistanceStatus,
} from "@store/tests/testSlice";
import {
  GazeData,
  PupilSizeData,
  EyeDisplacement,
  Snapshot,
  TestCategory,
  TestConfiguration,
  TestEventCodes,
  SnellenSize,
  TumblingEObject,
  TestStatus,
  TestProgressConfiguration,
  TestType,
  VFABehaviour,
  EyeType,
  NPCTSnapshot,
  NPCTUserInput,
  AntiSaccadeSample,
  SmoothPursuitSnapshot,
  OKNProgressSnapshot,
} from "types/testTypes";
import { RootState, store } from "@store/index";
import { selectSelectedPatient } from "@store/patients/patientSelectors";
import { selectIsAwaitingMenuSceneAfterCompletion } from "@utils/redux-utils/selectors/productSelectors";
import { Patient } from "types/patientTypes";
import i18n from "i18next";
import { openConfirmation } from "@actions/actions";
import { enableCalibrationForTest } from "@store/testSuite/testSuiteSlice";

import { MessageOpcode } from "types/MessageOpcode";
import { HeadsetVersion } from "types/productTypes";
import { BuildProgressConfigUpdatePayload, BuildTestStartPayload } from "./apiUtils";
import {
  selectMovingFixationCatchTrialsEnabled,
  selectReadingAssessmentPageChangeInteraction,
} from "@store/adminConfigurations/adminConfigurationsSelectors";
import { isVersionEnoughForStartWithCalibrationScene } from "@utils/VersionUtils";
import {
  ExtractCollectedVFADataFromTestString,
  ExtractVFAConfigurationFromTestString,
  ExtractVFASnapshotData,
  isVFASnapshot,
} from "@utils/test-specific-utils/PerimetryTestUtils";
import { TestSuiteSessionInfo, UnityVFATestStringResponse } from "types/api/payloadTypes";
import {
  buildColorVisionResumeCollectedData,
  buildColorVisionResumeConfiguration,
} from "@utils/test-specific-utils/ColorVisionResumeUtils";
import {
  buildContrastSensitivityResumeCollectedData,
  buildContrastSensitivityResumeConfiguration,
} from "@utils/test-specific-utils/ContrastSensitivityResumeUtils";
import { visualAcuityTestConfiguration } from "@constants/testConstants/testSpecificConstants/visualAcuityConstants";
import { getVirtualAssistantVideoListForTest } from "@constants/virtualAssistanceConstants";
import { parseDyscalculiaSnapshot } from "@utils/test-specific-utils/DyscalculiaTestUtils";
import { isVaCompletionState } from "@components/testContainer/common/vaOverlayState";
import {
  getSelectedTestName,
  selectCompletionPercentage,
  selectCurrentVirtualAssistanceVideo,
  selectTestId,
  selectTestPhase,
  selectTestStatus,
  selectVirtualAssistanceStatus,
} from "@store/tests/testSelectors";
import { normalizeControllerBattery } from "@utils/controllerBatteryUtils";
import { normalizeDevicePermissions } from "@utils/devicePermissions";
import {
  clearEyeTrackingFailure,
  setEyeTrackingFailure,
  setStartDeliveryReceipt,
  setSystemOverlayStatus,
  type StartDeliveryState,
} from "@store/connection/connectionSlice";
import {
  selectEyeTrackingFailure,
  selectIsSystemOverlayOpen,
} from "@store/connection/connectionSelectors";
import {
  hasEyeTrackingSignal,
  parseEyeTrackingRecoveredRun,
  parseEyeTrackingUnhealthyMessage,
  shouldPromptDeviceRestart,
} from "@utils/eyeTrackingHealthUtils";
import { foreignTestRunUuid } from "@utils/testRunOwnership";

export const actionStr = "OnMessage";

export function noteEyeTrackingSample(message: unknown) {
  if (!selectEyeTrackingFailure(store.getState())) return;
  if (!hasEyeTrackingSignal(message)) return;
  console.log("Eye tracking data is flowing again; clearing the notification");
  store.dispatch(clearEyeTrackingFailure());
}

const MONOTONIC_PROGRESS_TESTS: Set<TestCategory> = new Set([
  TestCategory.VisualField,
  TestCategory.OptokineticNystagmus,
]);

// Scenes that do NOT mean "the test has started", so leaving the assistant for
// one of them must not tear the VA window down: the assistant scene itself,
// the menu (test not started / aborted back to menu — handled by
// SCENE_SET_MENU), and idle (headset idle; VA may still resume).
const NON_TEST_SCENES = new Set(["VirtualAssistant", "Menu", "Idle"]);

function updateCalibrationLifecycleState(msg: WebSocketMessage) {
  const status = typeof msg["status"] === "string" ? msg["status"] : null;
  const calibrationLifecycle = {
    status,
    message: typeof msg["message"] === "string" ? msg["message"] : null,
    attempt: typeof msg["attempt"] === "number" ? msg["attempt"] : null,
    retryAttempt: typeof msg["retryAttempt"] === "number" ? msg["retryAttempt"] : null,
    success: typeof msg["success"] === "boolean" ? msg["success"] : null,
    outcome: typeof msg["outcome"] === "string" ? msg["outcome"] : null,
  };

  store.dispatch(setCalibrationLifecycle(calibrationLifecycle));
}

export function getProductId(): string {
  const productId = store.getState().product.pid;
  return productId ? String(productId) : "";
}

function broadcastCurrentProductScene(scene: string | null): void {
  const productId = getProductId();
  if (!productId) return;
  broadcastScene(scene, null, productId);
}

function syncSystemOverlayFromHeartbeat(msg: WebSocketMessage): void {
  const overlayOpen = msg["overlayOpen"];
  if (typeof overlayOpen !== "boolean") return;
  if (overlayOpen === selectIsSystemOverlayOpen(store.getState() as RootState)) return;
  if (!overlayOpen) {
    store.dispatch(setSystemOverlayStatus({ open: false }));
    return;
  }
  const scene = typeof msg["scene"] === "string" ? (msg["scene"] as string) : null;
  store.dispatch(setSystemOverlayStatus({ open: true, reason: "heartbeat", scene }));
}

function extractTumblingETestConfigurationFromCurrentDetails(
  currentTestDetails: Record<string, unknown>,
): TestConfiguration {
  const existingConfiguration = store.getState().test.configuration;
  const baseConfiguration =
    existingConfiguration?.name === TestCategory.VisualAcuity
      ? existingConfiguration
      : visualAcuityTestConfiguration;

  const testType =
    currentTestDetails["testType"] === TestType.TumblingEThreshold
      ? TestType.TumblingEThreshold
      : TestType.TumblingE;

  const eyeType =
    typeof currentTestDetails["eye"] === "string" &&
    Object.values(EyeType).includes(currentTestDetails["eye"] as EyeType)
      ? (currentTestDetails["eye"] as EyeType)
      : baseConfiguration.eyeConfiguration.eyeType;

  const configuration: TestConfiguration = {
    ...baseConfiguration,
    name: TestCategory.VisualAcuity,
    testType,
    eyeConfiguration: {
      ...baseConfiguration.eyeConfiguration,
      eyeType,
    },
  };

  if (configuration.virtualAssistanceConfig) {
    configuration.virtualAssistanceConfig = {
      ...configuration.virtualAssistanceConfig,
      videoInfos: getVirtualAssistantVideoListForTest(configuration),
    };
  }

  return configuration;
}

function extractCollectedTumblingEDataFromCurrentDetails(
  currentTestDetails: Record<string, unknown>,
) {
  const rawResults =
    currentTestDetails["results"] && typeof currentTestDetails["results"] === "object"
      ? (currentTestDetails["results"] as Record<string, unknown>)
      : null;

  const tumblingESnapshots = Array.isArray(rawResults?.["responses"])
    ? rawResults["responses"]
    : [];

  return {
    visualAcuitySnapshots: {
      tumblingESnapshots,
      currentlyActiveTumblingE:
        rawResults?.["currentStimulus"] && typeof rawResults["currentStimulus"] === "object"
          ? (rawResults["currentStimulus"] as TumblingEObject)
          : undefined,
      thresholdRow:
        rawResults?.["thresholdRow"] !== undefined
          ? (rawResults["thresholdRow"] as number | null)
          : undefined,
      thresholdSnellenSize:
        rawResults?.["thresholdSnellenSize"] !== undefined
          ? (rawResults["thresholdSnellenSize"] as SnellenSize | null)
          : undefined,
      chartStimuli: Array.isArray(rawResults?.["chartStimuli"])
        ? (rawResults["chartStimuli"] as TumblingEObject[])
        : undefined,
    },
  };
}

// CURRENT_TEST_DETAILS doubles as a probe ("which test is the device
// running?") and a resume trigger. The reply carries no request id, so the
// latest sender decides how the next reply is treated.
let currentTestDetailsProbeOnly = false;

export function setCurrentTestDetailsProbeOnly(probeOnly: boolean) {
  currentTestDetailsProbeOnly = probeOnly;
}

// Resuming an in-progress test from CURRENT_TEST_DETAILS is only implemented
// for these payloads; other tests keep running on the headset but the web
// cannot rebuild their progress page.
export function isResumableCurrentTestDetails(parsedTestString: Record<string, unknown>): boolean {
  const category = parsedTestString["category"];
  const testType = parsedTestString["testType"];
  if (category === "VFA" || category === "CV") {
    return true;
  }
  if (category === "CS") {
    return testType === TestType.Threshold || testType === TestType.Ascending;
  }
  if (category === "VA") {
    return testType === TestType.TumblingE || testType === TestType.TumblingEThreshold;
  }
  return false;
}

export const onMessageCallbacks: Map<MessageOpcode, (msg) => void> = new Map<
  MessageOpcode,
  (msg: WebSocketMessage) => void
>([
  [
    MessageOpcode.STATUS_READY,
    (msg) => {
      console.log("STATUS_READY message received:", msg);
      syncSystemOverlayFromHeartbeat(msg);
      const toBeUpdated = {};
      toBeUpdated["status"] = MessageOpcode.STATUS_READY;
      if ("battery" in msg) {
        toBeUpdated["battery"] = msg["battery"];
      }
      if ("controllers" in msg) {
        toBeUpdated["controllers"] = msg["controllers"];
      }
      if ("controllerBattery" in msg) {
        toBeUpdated["controllerBattery"] =
          normalizeControllerBattery(msg["controllerBattery"]) ?? null;
      }
      if ("version" in msg) {
        toBeUpdated["currentVersion"] = msg["version"];
      }
      if ("puiVersion" in msg) {
        toBeUpdated["osVersion"] = msg["puiVersion"];
      }
      if ("states" in msg) {
        try {
          const parsedStates =
            typeof msg["states"] === "string" ? JSON.parse(msg["states"]) : msg["states"];
          if (
            parsedStates &&
            parsedStates.permissions &&
            typeof parsedStates.permissions === "object"
          ) {
            toBeUpdated["permissions"] = normalizeDevicePermissions(parsedStates.permissions);
          }
        } catch (e) {
          console.warn("Failed to parse states from STATUS_READY", e);
        }
      }
      if ("volume" in msg) {
        const volumeValue =
          typeof msg["volume"] === "number"
            ? msg["volume"]
            : typeof msg["volume"] === "string"
              ? parseInt(msg["volume"], 10)
              : null;
        if (volumeValue !== null && !isNaN(volumeValue) && volumeValue >= 0) {
          toBeUpdated["volume"] = volumeValue;
          store.dispatch(updateVolume(volumeValue));
        }
      }

      store.dispatch(updateProductBatch(toBeUpdated));
    },
  ],
  [
    MessageOpcode.SCENE_SET_TEST,
    (msg) => {
      console.log(msg);
      const sceneName = msg["scene"] as string;
      const isTutorialActive = msg["isTutorialActive"] === true;
      const isCalibrationScene =
        typeof sceneName === "string" && sceneName.toLowerCase().includes("calibration");
      store.dispatch(updateStatus(MessageOpcode.SCENE_SET_TEST));
      if (sceneName) {
        store.dispatch(updateScene(sceneName, new Date()));
        broadcastCurrentProductScene(sceneName);
      }
      // Most SCENE_SET_TEST pushes (including the tutorial's) omit "scene" --
      // it only changes when a test explicitly reports one -- so the device
      // status must still be recomputed from whatever scene is already known,
      // or a message that's only carrying isTutorialActive never takes effect.
      const knownScene = sceneName || (store.getState() as RootState).product.scene;
      store.dispatch(updateDeviceStatus(statusFromScene(knownScene, isTutorialActive)));
      if (!isCalibrationScene) {
        store.dispatch(setCalibrationLifecycle(null));
      }
      // The tutorial runs inline within the real test scene before the real
      // test itself begins, so a scene push while it's active must not flip
      // testStatus to Started -- that would start the real test's timer
      // before the patient has even finished the practice step.
      if (isTutorialActive) {
        return;
      }
      // Only flip testStatus when this page is in a test flow (a test it is
      // following, or a patient mid-configuration). A scene push for a test
      // started elsewhere must not yank the patient-selection screen into an
      // empty progress view — the InTestRecoveryCard handles that case.
      const sceneState = store.getState() as RootState;
      const sceneFlowStatus = selectTestStatus(sceneState);
      const staleAfterCompletion =
        (sceneFlowStatus === TestStatus.TestConfiguration ||
          sceneFlowStatus === TestStatus.StartRequested) &&
        selectIsAwaitingMenuSceneAfterCompletion(sceneState);
      const onNonTestScreen =
        sceneFlowStatus === TestStatus.DeviceSelection ||
        sceneFlowStatus === TestStatus.PatientSelection;
      const inTestFlow =
        !staleAfterCompletion &&
        !onNonTestScreen &&
        (isFollowingTest() || selectSelectedPatient(sceneState) != null);
      if (!inTestFlow) {
        return;
      }

      // STOP_TEST is rejected while the Tobii calibration scene is live. Keep
      // the operator's termination request authoritative when a later scene
      // push arrives: a repeated calibration push must not flip the UI back to
      // Started, and the first non-calibration scene is the earliest safe
      // point at which the headset can accept the pending stop.
      if (selectTestStatus(sceneState) === TestStatus.TerminateRequested) {
        if (!isCalibrationScene) {
          store.dispatch(sendTestTerminateMessage({ isRetry: true }));
        }
        return;
      }

      if (sceneName === "VirtualAssistant") {
        store.dispatch(setTestStatus({ status: TestStatus.VirtualAssistant }));
      } else {
        store.dispatch(setTestStatus({ status: TestStatus.Started }));
      }
    },
  ],
  [
    MessageOpcode.SCENE_SET_MENU,
    (msg) => {
      console.log(msg);
      const currentTestStatus = selectTestStatus(store.getState() as RootState);
      const keepTestUi =
        currentTestStatus === TestStatus.Completed ||
        currentTestStatus === TestStatus.CompleteRequested ||
        currentTestStatus === TestStatus.StartRequested ||
        currentTestStatus === TestStatus.DeviceSelection ||
        currentTestStatus === TestStatus.PatientSelection;
      store.dispatch(updateStatus(MessageOpcode.SCENE_SET_MENU));
      clearHeadsetRebootPending();
      store.dispatch(updateScene("Menu", new Date()));
      store.dispatch(updateDeviceStatus(statusFromScene("Menu")));
      broadcastCurrentProductScene("Menu");
      store.dispatch(setCalibrationLifecycle(null));
      store.dispatch(setIsCurrentlyCalibrating(false));
      store.dispatch(setHeadsetActiveTest(null));
      store.dispatch(setTestPhase(null));
      if (!keepTestUi) {
        store.dispatch(setTestStatus({ status: TestStatus.TestConfiguration }));
        store.dispatch(clearTestProgress());
      }
    },
  ],
  [
    MessageOpcode.CALIBRATION_UPDATE,
    (msg) => {
      updateCalibrationLifecycleState(msg);
    },
  ],
  [
    MessageOpcode.UPDATE_STATUS,
    (msg) => {
      console.log(msg);
      const toBeUpdated = {};
      toBeUpdated["status"] = MessageOpcode.UPDATE_STATUS;

      if (isVFASnapshot(msg)) {
        handleIncomingUpdateMessage(msg);
      } else if (("finishPercent" in msg || "finish" in msg) && !isForeignTestMessage(msg)) {
        checkTestFinishPercent(msg);
        checkTestCompletion(msg);
      }

      const isSceneReady = msg["isSceneReady"];
      if (isSceneReady !== undefined) {
        store.dispatch(setIsSceneReady(isSceneReady));
      }

      if ("testPhase" in msg) {
        const tp = msg["testPhase"];
        store.dispatch(setTestPhase(typeof tp === "string" ? tp : null));
      }

      if ("gazeDistanceFromCenter" in msg) {
        const testStatus = selectTestStatus(store.getState());
        if (testStatus !== TestStatus.Started && testStatus !== TestStatus.CompleteRequested) {
          return;
        }
        const gazeDistanceFromCenter = msg["gazeDistanceFromCenter"] as EyeDisplacement;
        store.dispatch(updateCollectedTestData({ gazeDistanceFromCenter } as Snapshot));
        return;
      }
      if ("timescale" in msg) {
        store.dispatch(updateTimeScale(msg["timescale"] as number));
      }
      if ("calibrationStatus" in msg) {
        switch (msg["calibrationStatus"]) {
          case "InProgress": {
            store.dispatch(setIsCurrentlyCalibrating(true));
            break;
          }
          case "Finished": {
            store.dispatch(setIsCurrentlyCalibrating(false));
            break;
          }
          default: {
            console.warn("Unhandled calibration status:", msg["calibrationStatus"]);
            store.dispatch(setIsCurrentlyCalibrating(false));
            break;
          }
        }
      }

      if ("pupilSize" in msg) {
        const pupilSizeData = msg["pupilSize"] as PupilSizeData;
        store.dispatch(updatePupilSizeData(pupilSizeData));
      } else if ("gazeDataPoint" in msg) {
        const gazeData = msg["gazeDataPoint"] as GazeData;
        store.dispatch(updateGazeData(gazeData));
      }

      if ("isGazeOffBounds" in msg) {
        store.dispatch(setIsGazeOffBounds(msg["isGazeOffBounds"] as boolean));
      }

      if ("version" in msg) {
        toBeUpdated["version"] = msg["version"];
      }
      if ("isEyeTrackingOn" in msg) {
        toBeUpdated["isEyeTrackingOn"] = msg["isEyeTrackingOn"];
      }
      if ("controllers" in msg) {
        toBeUpdated["controllers"] = msg["controllers"];
      }
      store.dispatch(updateProductBatch(toBeUpdated));
    },
  ],
  [
    MessageOpcode.ACTION,
    (msg) => {
      console.log(msg);
    },
  ],
  [
    MessageOpcode.DEVICE_CONFIG,
    (msg) => {
      if ("volume" in msg && typeof msg["volume"] === "number") {
        const volumeValue = msg["volume"] as number;
        store.dispatch(updateVolume(volumeValue));
      }
    },
  ],
  [
    MessageOpcode.STATE,
    (msg) => {
      const state = store.getState() as RootState;
      console.log("State message received. Message: ", msg);
      syncSystemOverlayFromHeartbeat(msg);
      if (
        "version" in msg &&
        msg["version"] !== "" &&
        msg["version"] !== state.product.currentVersion
      ) {
        store.dispatch(updateCurrentVersion(msg["version"] as HeadsetVersion));
      }
      if ("scene" in msg) {
        const incomingScene =
          msg["scene"] === "Test" || msg["scene"] === "Menu"
            ? (msg["scene"] as string)
            : msg["scene"].toString();
        const isTutorialActive = msg["isTutorialActive"] === true;
        if (incomingScene === "Menu") {
          clearHeadsetRebootPending();
        }
        store.dispatch(updateScene(incomingScene, new Date()));
        store.dispatch(updateDeviceStatus(statusFromScene(incomingScene, isTutorialActive)));
        broadcastCurrentProductScene(incomingScene);

        // Normal Virtual Assistant (welcome) videos only ever play inside the
        // dedicated VirtualAssistant scene. So once the headset enters an
        // actual test scene the assistant flow is over and the window must
        // close — even if the web is still (futilely) trying to load a video on
        // a slow connection, and even if the completion push was lost so the
        // test status already moved on. Menu/Idle are excluded: they are not
        // test scenes (test not started / headset idle), so they do not signal
        // that VA finished. Guide/instruction videos are also exempt: they are
        // in-scene overlays that legitimately play during the test scene, so
        // leave them alone (identified by isGuideVideo). The presence checks
        // also stop us re-dispatching on every heartbeat once torn down.
        const vaStatus = selectVirtualAssistanceStatus(state);
        const isGuideVideo = vaStatus?.isGuideVideo === true;
        const vaStatusActive = vaStatus?.state != null && !isVaCompletionState(vaStatus.state);
        const vaWindowPresent =
          selectTestStatus(state) === TestStatus.VirtualAssistant ||
          selectCurrentVirtualAssistanceVideo(state) != null ||
          vaStatusActive;
        if (!NON_TEST_SCENES.has(incomingScene) && vaWindowPresent && !isGuideVideo) {
          if (selectTestStatus(state) === TestStatus.VirtualAssistant) {
            store.dispatch(setTestStatus({ status: TestStatus.Started }));
          }
          store.dispatch(setCurrentVirtualAssistanceVideo(null));
          // Mark VA as done rather than nulling the status, so the reducer
          // preserves doNotShowAgainList (matching the normal completion flow).
          // A completion state lets isVaOverlayHidden tear the window down.
          store.dispatch(setVirtualAssistanceStatus({ state: "NoVideoPlaying" }));
        }
      }
      if ("controllers" in msg) {
        store.dispatch(updateControllers(msg["controllers"] as number));
      }
      if ("controllerBattery" in msg) {
        store.dispatch(
          updateControllerBattery(normalizeControllerBattery(msg["controllerBattery"])),
        );
      }
      if ("volume" in msg) {
        const volumeValue =
          typeof msg["volume"] === "number"
            ? msg["volume"]
            : typeof msg["volume"] === "string"
              ? parseInt(msg["volume"], 10)
              : null;
        if (volumeValue !== null && !isNaN(volumeValue) && volumeValue >= 0) {
          store.dispatch(updateVolume(volumeValue));
        }
      }
      if ("battery" in msg) {
        const batteryValue =
          typeof msg["battery"] === "number"
            ? msg["battery"]
            : typeof msg["battery"] === "string"
              ? parseInt(msg["battery"], 10)
              : null;
        if (
          batteryValue !== null &&
          !isNaN(batteryValue) &&
          batteryValue >= 0 &&
          batteryValue <= 100
        ) {
          store.dispatch(updateBattery(batteryValue));
        }
      }
      if ("testPhase" in msg) {
        const tp = msg["testPhase"];
        store.dispatch(setTestPhase(typeof tp === "string" ? tp : null));
      }
    },
  ],
  [
    MessageOpcode.CURRENT_TEST_DETAILS,
    (msg) => {
      console.log("current test details", msg);
      const probeOnly = currentTestDetailsProbeOnly;
      currentTestDetailsProbeOnly = false;
      if ("testPhase" in msg) {
        const tp = msg["testPhase"];
        const phase = typeof tp === "string" ? tp : null;
        store.dispatch(setTestPhase(phase));
        if (phase !== null && phase !== "WaitingForPatient") {
          store.dispatch(setIsSceneReady(true));
        }
      }
      // Headsets that report it include the live gaze state in the reply;
      // without this a page resuming mid-fixation-loss never learns the gaze
      // is off bounds (the periodic updates only fire on transitions).
      if (typeof msg["isGazeOffBounds"] === "boolean") {
        store.dispatch(setIsGazeOffBounds(msg["isGazeOffBounds"]));
      }
      if (msg["test"]) {
        try {
          const parsedTestString = JSON.parse(msg["test"]) as Record<string, unknown>;
          const category = parsedTestString["category"];
          store.dispatch(
            setHeadsetActiveTest({
              category: typeof category === "string" ? category : null,
              isResumable: isResumableCurrentTestDetails(parsedTestString),
            }),
          );
          if (probeOnly) {
            return;
          }
          const startDate =
            typeof parsedTestString["startDate"] === "string"
              ? parsedTestString["startDate"]
              : null;
          const testId =
            typeof parsedTestString["uuid"] === "string" ? parsedTestString["uuid"] : null;

          if (category === "VFA") {
            const vfaTestString = parsedTestString as unknown as UnityVFATestStringResponse;
            const existingVfaConfiguration =
              store.getState().test.configuration?.name === TestCategory.VisualField
                ? store.getState().test.configuration
                : null;
            const testConfig = ExtractVFAConfigurationFromTestString(
              vfaTestString,
              existingVfaConfiguration,
            );
            const collectedVFAData = ExtractCollectedVFADataFromTestString(vfaTestString);
            const hasPointData = collectedVFAData?.pointValues?.length > 0;

            store.dispatch(
              resumeTest({
                testName: TestCategory.VisualField,
                testConfiguration: testConfig,
                collectedData: hasPointData ? { vfaCollectedData: collectedVFAData } : null,
                startDate,
                testId,
              }),
            );
            return;
          }

          const testType = parsedTestString["testType"];
          const isTumblingETest =
            testType === TestType.TumblingE || testType === TestType.TumblingEThreshold;

          if (category === "CV") {
            const existingColorVisionConfiguration =
              store.getState().test.configuration?.name === TestCategory.ColorVision
                ? store.getState().test.configuration
                : null;
            store.dispatch(
              resumeTest({
                testName: TestCategory.ColorVision,
                testConfiguration: buildColorVisionResumeConfiguration(
                  parsedTestString,
                  existingColorVisionConfiguration,
                ),
                collectedData: buildColorVisionResumeCollectedData(parsedTestString),
                startDate,
                testId,
              }),
            );
            return;
          }

          if (
            category === "CS" &&
            (parsedTestString["testType"] === TestType.Threshold ||
              parsedTestString["testType"] === TestType.Ascending)
          ) {
            const existingContrastSensitivityConfiguration =
              store.getState().test.configuration?.name === TestCategory.ContrastSensitivity
                ? store.getState().test.configuration
                : null;
            store.dispatch(
              resumeTest({
                testName: TestCategory.ContrastSensitivity,
                testConfiguration: buildContrastSensitivityResumeConfiguration(
                  parsedTestString,
                  existingContrastSensitivityConfiguration,
                ),
                collectedData: buildContrastSensitivityResumeCollectedData(parsedTestString),
                startDate,
                testId,
              }),
            );
            return;
          }

          if (category === "VA" && isTumblingETest) {
            store.dispatch(
              resumeTest({
                testName: TestCategory.VisualAcuity,
                testConfiguration:
                  extractTumblingETestConfigurationFromCurrentDetails(parsedTestString),
                collectedData: extractCollectedTumblingEDataFromCurrentDetails(parsedTestString),
                startDate,
                testId,
              }),
            );
          }
        } catch (error) {
          console.error("Failed to parse CURRENT_TEST_DETAILS payload:", error, msg["test"]);
        }
      }
    },
  ],
  [
    MessageOpcode.VA_TEST_UPDATE,
    (msg) => {
      // The run uuid may sit on the envelope rather than in the parsed test.
      if (isForeignTestMessage(msg)) return;
      const test = msg["test"] ? JSON.parse(msg["test"]) : null;
      if (test) {
        handleIncomingUpdateMessage(test);
      } else {
        handleIncomingUpdateMessage(msg);
      }
    },
  ],
  [
    MessageOpcode.COLOR_TEST_UPDATE,
    (msg) => {
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.CONTRAST_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.CONTRAST_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.DYSLEXIA_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.DYSLEXIA_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.PUPILLOMETRY_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.PUPILLOMETRY_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.FDT_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.FDT_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.CONTINUOUS_PERFORMANCE_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.CONTINUOUS_PERFORMANCE_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.COVER_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.COVER_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.STEREO_ACUITY_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.STEREO_ACUITY_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.VIDEONYSTAGMOGRAPHY_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.VIDEONYSTAGMOGRAPHY_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.KINETIC_TEST_UPDATE,
    (msg) => {
      if (msg["kineticStimulus"]) {
        store.dispatch(updateStatus(MessageOpcode.KINETIC_TEST_UPDATE));
      }
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.DYSCALCULIA_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.DYSCALCULIA_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.WCST_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.WCST_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.READING_ASSESSMENT_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.READING_ASSESSMENT_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.NPCT_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.NPCT_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.OKN_TEST_UPDATE,
    (msg) => {
      store.dispatch(updateStatus(MessageOpcode.OKN_TEST_UPDATE));
      handleIncomingUpdateMessage(msg);
    },
  ],
  [
    MessageOpcode.VIRTUAL_ASSISTANCE_VIDEO_UPDATE,
    (msg) => {
      console.log("Virtual Assistance Video Update:", msg);

      const statusPayload: VirtualAssistanceStatus = {
        state: (msg["state"] as string) ?? null,
        remainingVideos:
          typeof msg["remainingVideos"] === "number" ? (msg["remainingVideos"] as number) : null,
        currentRetryCount:
          typeof msg["currentRetryCount"] === "number"
            ? (msg["currentRetryCount"] as number)
            : null,
        maxRetryCount:
          typeof msg["maxRetryCount"] === "number" ? (msg["maxRetryCount"] as number) : null,
        isRetrySequence:
          typeof msg["isRetrySequence"] === "boolean"
            ? (msg["isRetrySequence"] as boolean)
            : undefined,
        isInfiniteRetry:
          typeof msg["isInfiniteRetry"] === "boolean"
            ? (msg["isInfiniteRetry"] as boolean)
            : undefined,
        retryVideoName:
          typeof msg["retryVideoName"] === "string"
            ? (msg["retryVideoName"] as string) || null
            : null,
        englishFallbackUsed: msg["englishFallbackUsed"] === true || undefined,
        isGuideVideo: msg["isGuideVideo"] === true || undefined,
      };

      const hasStatusData = Object.values(statusPayload).some(
        (value) => value !== null && value !== undefined,
      );

      store.dispatch(setVirtualAssistanceStatus(hasStatusData ? statusPayload : null));

      const shouldClearVideo = isVaCompletionState(statusPayload.state ?? null);

      if (shouldClearVideo) {
        store.dispatch(setCurrentVirtualAssistanceVideo(null));
      } else {
        const nextVideoName =
          msg["currentVideoName"] || msg["currentVideo"] || msg["videoName"] || null;

        if (typeof nextVideoName === "string" && nextVideoName.trim().length > 0) {
          store.dispatch(setCurrentVirtualAssistanceVideo(nextVideoName));
        }
      }

      if (msg["remainingVideos"]) {
        console.log("Remaining videos in sequence:", msg["remainingVideos"]);
      }
      if (msg["isRetrySequence"]) {
        console.log("Is retry sequence:", msg["isRetrySequence"]);
      }
      if (msg["state"]) {
        console.log("Virtual assistance state:", msg["state"]);
      }
    },
  ],
  [
    MessageOpcode.TEST_START_ACK,
    (msg) => {
      // Newer headset builds confirm receipt of a start command immediately,
      // before the test scene finishes loading.
      const uuid = typeof msg["uuid"] === "string" ? (msg["uuid"] as string) : null;
      if (!uuid) {
        console.warn("Ignoring TEST_START_ACK without uuid");
        return;
      }
      const failed = msg["failed"] === true;
      const reason = typeof msg["reason"] === "string" ? msg["reason"] : null;

      if (failed) {
        console.warn("TEST_START_ACK reports a failed test start for:", uuid, reason);
        store.dispatch(setStartDeliveryReceipt({ uuid, state: "rejected", reason }));
        return;
      }

      console.log("TEST_START_ACK received for test:", uuid);
      store.dispatch(setStartDeliveryReceipt({ uuid, state: "acked" }));
    },
  ],
  [
    MessageOpcode.SYSTEM_OVERLAY,
    (msg) => {
      const open = msg["overlayOpen"] === true;
      const reason = typeof msg["reason"] === "string" ? msg["reason"] : null;
      const scene = typeof msg["scene"] === "string" ? msg["scene"] : null;
      store.dispatch(setSystemOverlayStatus({ open, reason, scene }));
    },
  ],
  [
    MessageOpcode.HEADSET_REMOVED,
    (msg) => {
      const state = store.getState();
      const { tests, activeTestIndex, calibrationEnabledTestId } = state.testSuite;
      const removedAt = Number(msg["removedAt"]);
      if (
        activeTestIndex === null ||
        activeTestIndex < 0 ||
        activeTestIndex >= tests.length ||
        !Number.isFinite(removedAt) ||
        removedAt <= 0
      ) {
        return;
      }

      // The removal is critical and may be flushed after reconnect. Only act
      // while it still refers to the active run; if the run has advanced, the
      // "next test" resolved from activeTestIndex would be the wrong one.
      const startUuid = typeof msg["startUuid"] === "string" ? msg["startUuid"] : null;
      if (!startUuid || startUuid !== selectTestId(state)) {
        return;
      }

      const terminalStatuses = new Set(["completed", "terminated", "skipped", "error"]);
      const nextTest = tests.find(
        (test, index) => index > activeTestIndex && !terminalStatuses.has(test.status),
      );
      if (!nextTest || nextTest.id === calibrationEnabledTestId) {
        return;
      }

      store.dispatch(enableCalibrationForTest(nextTest.id));
      store.dispatch(
        openConfirmation(
          i18n.t("TestController.headsetRemovedCalibrationTitle"),
          i18n.t("TestController.headsetRemovedCalibrationMessage"),
          () => {},
          true,
          "close",
          "",
        ),
      );
    },
  ],
  [
    MessageOpcode.EYE_TRACKING_UNHEALTHY,
    (msg) => {
      const state = store.getState();
      const existing = selectEyeTrackingFailure(state);

      const recoveredRun = parseEyeTrackingRecoveredRun(msg);
      if (recoveredRun) {
        if (existing && recoveredRun === existing.startUuid) {
          console.log("Headset reports eye tracking recovered; clearing the notification");
          store.dispatch(clearEyeTrackingFailure());
        }
        return;
      }

      const report = parseEyeTrackingUnhealthyMessage(msg);
      if (!report) return;
      if (existing && (existing.severity === "error" || existing.severity === report.severity)) {
        return;
      }

      if (
        !shouldPromptDeviceRestart({
          report,
          testCategory: state.test?.configuration?.name ?? null,
          testStatus: selectTestStatus(state),
          activeTestId: selectTestId(state),
        })
      ) {
        return;
      }

      console.warn(`Headset reports eye tracking unhealthy (${report.severity}):`, report);
      store.dispatch(
        setEyeTrackingFailure({
          startUuid: report.startUuid,
          recoveryAttempts: report.recoveryAttempts,
          scene: report.scene,
          reason: report.reason,
          severity: report.severity,
        }),
      );
    },
  ],
]);

/**
 * Delivery receipts from newer backends for device-bound commands. Sent in
 * place of the legacy behavior of silently dropping messages when the headset
 * socket is missing.
 */
export function handleRelayReceipt(message: {
  event: string;
  uuid?: string;
  reason?: string;
  duplicate?: boolean;
}) {
  const uuid = typeof message.uuid === "string" ? message.uuid : null;
  if (!uuid) {
    // Receipts for uuid-less commands (config, controller reconnects...)
    // must not clobber the in-flight start receipt.
    return;
  }
  let state: StartDeliveryState;
  if (message.event === "relay-failed") {
    state = "failed";
  } else if (message.duplicate === true) {
    state = "duplicate";
  } else {
    state = "delivered";
  }
  console.log("Relay receipt:", state, "uuid:", uuid, "reason:", message.reason ?? "-");
  store.dispatch(setStartDeliveryReceipt({ uuid, state, reason: message.reason ?? null }));
}

/**
 * Server-pushed test run snapshot (on web connect, resume, and whenever the
 * backend observes completion — including via the persisted result that
 * legacy headsets upload over REST). A "test run" is a single test execution
 * (one start command / one uuid), not the multi-test suite session.
 * Reconciles a UI that missed the finish message and is stuck showing a
 * running test.
 */
export function handleTestRunStateEvent(
  testRun: {
    uuid?: string;
    status?: string;
    finishPercent?: number | null;
  },
  attempt = 0,
) {
  if (!testRun || typeof testRun.uuid !== "string") {
    return;
  }
  const state = store.getState();
  const currentTestId = selectTestId(state);
  const testStatus = selectTestStatus(state);
  console.log("Test run state received:", testRun.status, "uuid:", testRun.uuid);

  if (testRun.uuid !== currentTestId || testRun.status !== "completed") {
    return;
  }
  const uiThinksRunning =
    testStatus === TestStatus.Started || testStatus === TestStatus.CompleteRequested;
  if (!uiThinksRunning) {
    return;
  }
  // Only reconcile a quiet UI: if test messages are still streaming in, the
  // live flow wins. Re-check once after the quiet window so a snapshot that
  // raced replayed messages is not lost.
  const lastMessageAt = getWSService().lastMessageAt ?? 0;
  const quietMs = Date.now() - lastMessageAt;
  if (quietMs < 10000) {
    if (attempt === 0) {
      setTimeout(() => handleTestRunStateEvent(testRun, 1), 12000);
    }
    return;
  }
  if (attempt < 2) {
    // Quiet right now, but the snapshot may still be ahead of in-flight
    // relayed/replayed messages (e.g. right after a reconnect); give them a
    // moment to land before overriding the UI.
    setTimeout(() => handleTestRunStateEvent(testRun, 2), 2000);
    return;
  }
  console.warn("Reconciling stuck test UI to Completed from server test run state:", testRun.uuid);
  store.dispatch(setTestStatus({ status: TestStatus.Completed }));
}

// A late finish or progress message of an earlier run (or of another page's
// run on the same headset) must not end or move the test on screen. Only
// judged while the page follows a test; otherwise the handlers drop it anyway.
function isForeignTestMessage(msg: WebSocketMessage): boolean {
  if (!isFollowingTest()) return false;
  const currentTestId = selectTestId(store.getState());
  const foreignUuid = foreignTestRunUuid(msg, currentTestId);
  if (!foreignUuid) return false;
  console.warn(
    "Ignoring test message for another test run:",
    foreignUuid,
    "current:",
    currentTestId,
  );
  return true;
}

function handleIncomingUpdateMessage(msg: WebSocketMessage) {
  if (isForeignTestMessage(msg)) return;
  const testStatus = selectTestStatus(store.getState());
  if (testStatus !== TestStatus.Started && testStatus !== TestStatus.CompleteRequested) {
    return;
  }
  if (selectTestPhase(store.getState()) === "WaitingForPatient") {
    store.dispatch(setTestPhase("Testing"));
  }
  const snapshot = extractSnapshotData(msg);
  store.dispatch(updateCollectedTestData(snapshot));
  checkTestFinishPercent(msg);
  checkTestCompletion(msg);
}

// Statuses in which this page is actually following a test. Finish/progress
// messages pushed by a headset for a test the page is NOT tracking (e.g. the
// user sits on patient selection while an old test wraps up) must be ignored —
// honoring them yanks the UI into an empty "Test Finished!" progress view.
const TRACKED_TEST_STATUSES = new Set<TestStatus>([
  TestStatus.StartRequested,
  TestStatus.Calibration,
  TestStatus.VirtualAssistant,
  TestStatus.Started,
  TestStatus.Paused,
  TestStatus.CompleteRequested,
  TestStatus.Completed,
  TestStatus.WaitingForNextEye,
]);

function isFollowingTest(): boolean {
  return TRACKED_TEST_STATUSES.has(selectTestStatus(store.getState()));
}

function checkTestFinishPercent(msg: WebSocketMessage) {
  if (!isFollowingTest()) return;
  if (selectTestStatus(store.getState()) === TestStatus.StartRequested) return;
  const snapshot = msg["snapshot"] as Record<string, unknown> | undefined;
  const snapshotProgressPercent =
    snapshot && typeof snapshot["progressPercent"] === "number"
      ? (snapshot["progressPercent"] as number)
      : undefined;
  const finishPercent =
    "finishPercent" in msg && typeof msg["finishPercent"] === "number"
      ? (msg["finishPercent"] as number)
      : snapshotProgressPercent;

  if (typeof finishPercent === "number") {
    const currentFinishPercent = selectCompletionPercentage(store.getState());
    const currentTest = getSelectedTestName(store.getState());
    if (currentTest === TestCategory.VisualField && finishPercent === 100) {
      store.dispatch(setTestStatus({ status: TestStatus.Completed }));
      return;
    }
    if (MONOTONIC_PROGRESS_TESTS.has(currentTest) && finishPercent < currentFinishPercent) {
      return;
    }
    store.dispatch(setTestCompletedPercentage(finishPercent));
  }
}

function checkTestCompletion(msg: WebSocketMessage) {
  if (!isFollowingTest()) return;
  if (selectTestStatus(store.getState()) === TestStatus.StartRequested) return;
  const snapshot = msg["snapshot"] as Record<string, unknown> | undefined;
  if (msg["finish"] === true || snapshot?.["finish"] === true) {
    if (msg["nextEyeToTest"]) {
      store.dispatch(startTestingNextEyeInBackToBackTest(msg["nextEyeToTest"] as EyeType));
    } else {
      store.dispatch(setTestStatus({ status: TestStatus.Completed }));
      store.dispatch(setIsLastBackToBackEyeTest(false));
    }
  }
}

export function sendWSMessage(message: WebSocketMessage) {
  message.action = actionStr;
  getWSService().sendMessage(message.toDict());
}

export function sendWSMessageJson(json: {}) {
  json["action"] = actionStr;
  if (json["opcode"] !== undefined && json["opcode"] !== null) {
    json["opcode"] = json["opcode"].toString();
  } else {
    console.error("sendWSMessageJson: opcode is undefined or null", json);
    return;
  }
  const ws_ = getWSService();
  ws_.sendMessage(json);
}

export function sendStatusRequested(pid) {
  sendWSMessage(
    new WebSocketMessage({ opcode: MessageOpcode.STATUS_REQUEST, action: actionStr, pid: pid }),
  );
}

export function sendReconnectControllersMessage() {
  const pid = getProductId();

  if (!pid) {
    console.warn("Should not be able to request controller reconnect: missing productId");
  }

  sendWSMessage(
    new WebSocketMessage({
      opcode: MessageOpcode.RECONNECT_CONTROLLERS,
      action: actionStr,
      pid,
    }),
  );
}

export function sendCalibrate() {
  const pid = getProductId();
  if (!pid) {
    console.warn("Cannot send calibrate: missing productId");
    return;
  }
  sendWSMessageJson({
    opcode: MessageOpcode.CALIBRATE,
    action: actionStr,
    pid,
  });
}

export function sendStopTest() {
  sendWSMessage(
    new WebSocketMessage({
      opcode: MessageOpcode.STOP_TEST,
      action: actionStr,
      pid: getProductId(),
    }),
  );
}

export function sendStatusStart(
  testTypeStr: string,
  testId: string,
  eye: string,
  patient: Patient,
  behaviour: VFABehaviour,
  testConfig: ITestAlgoConfig,
  showTutorial: boolean,
) {
  sendWSMessageJson({
    opcode: MessageOpcode.STATUS_START,
    action: actionStr,
    pid: getProductId(),
    uuid: testId,
    eye: eye,
    testType: testTypeStr,
    age: calculateAgeForTest(new Date(), parseDateOnly(patient.dob)),
    behaviour: behaviour,
    testConfig: testConfig,
    showTutorial: showTutorial,
    patient: patient,
    orgId: getOrganizationId(),
  });
}

export function sendShowChartsMessage() {
  sendWSMessage(
    new WebSocketMessage({
      opcode: MessageOpcode.GO_TO_CHARTS,
      action: actionStr,
      pid: getProductId(),
    }),
  );
}

export function sendChangeConfigMessage(testConfig: TestAlgoConfig) {
  sendWSMessageJson({
    opcode: MessageOpcode.UPDATE_CONFIG,
    action: actionStr,
    pid: getProductId(),
    testConfig: testConfig,
  });
}

export function sendProgressConfigurationUpdateMessage(
  testConfig: TestProgressConfiguration,
  testName: TestCategory,
) {
  const payload = BuildProgressConfigUpdatePayload(
    testConfig,
    testName,
    store.getState().product.currentVersion,
    selectMovingFixationCatchTrialsEnabled(store.getState()),
  );
  sendWSMessageJson({
    opcode: MessageOpcode.UPDATE_CONFIG,
    action: actionStr,
    pid: getProductId(),
    ...payload,
  });
}

// The relay serves one web client per device; reconnecting takes that slot
// back, so the reply and the headset's progress reach this page again.
export function reconnectAndRequestCurrentTestDetails() {
  getWSService().reconnect();
  sendCurrentTestDetailsMessage();
}

export function sendCurrentTestDetailsMessage(options?: { probeOnly?: boolean }) {
  setCurrentTestDetailsProbeOnly(options?.probeOnly === true);
  sendWSMessageJson({
    opcode: MessageOpcode.CURRENT_TEST_DETAILS,
    action: actionStr,
    pid: getProductId(),
  });
}

export function sendGenericNextMessage(
  eventCodes: TestEventCodes,
  productId: string,
  organiaztionId: string,
  testId: string,
  extraData?: any,
) {
  if (!eventCodes.nextMessage) {
    throw new Error("nextMessage is not defined in eventCodes");
  }
  sendWSMessageJson({
    opcode: eventCodes.nextMessage.valueOf(),
    action: actionStr,
    pid: productId,
    orgId: organiaztionId,
    uuid: testId,
    ...extraData,
  });
}

export function sendTestStartRequest(
  eventCodes: TestEventCodes,
  pid: string,
  orgId: string,
  testId: string,
  testConfig: TestConfiguration,
  patient: Patient,
  suiteSessionInfo?: TestSuiteSessionInfo,
) {
  const currentVersion = store.getState().product.currentVersion;
  const supportsCalibrationStartScene = isVersionEnoughForStartWithCalibrationScene(currentVersion);
  const payload = BuildTestStartPayload(
    eventCodes.startMessage.valueOf(),
    pid,
    orgId,
    testId,
    patient,
    calculateAgeForTest(new Date(), new Date(patient.dob)),
    testConfig,
    actionStr,
    suiteSessionInfo,
    selectReadingAssessmentPageChangeInteraction(store.getState()),
    currentVersion,
    selectMovingFixationCatchTrialsEnabled(store.getState()),
  );
  if (!supportsCalibrationStartScene) {
    delete payload.startWithCalibrationScene;
  }
  // Reset the delivery receipt for this attempt; newer backends answer with
  // relay-delivered / relay-failed, legacy backends leave it pending.
  store.dispatch(setStartDeliveryReceipt({ uuid: testId, state: "pending" }));
  sendWSMessageJson(payload);
}

export function sendTestFinishRequest(
  eventCodes: TestEventCodes,
  productId: string,
  testId: string,
  extraData?: any,
) {
  if (!eventCodes.nextMessage) {
    console.error("nextMessage is not defined in eventCodes");
    return;
  }
  sendWSMessageJson({
    opcode: eventCodes.nextMessage.valueOf(),
    pid: productId,
    action: actionStr,
    uuid: testId,
    finish: true,
    ...extraData,
  });
}

export function sendTestTerminateRequest(productId: string, testId: string) {
  sendWSMessageJson({
    opcode: MessageOpcode.STOP_TEST,
    action: actionStr,
    pid: productId,
    uuid: testId,
  });
}

export function sendVolumeSet(volume: number) {
  sendWSMessageJson({
    opcode: MessageOpcode.DEVICE_CONFIG,
    action: actionStr,
    pid: getProductId(),
    volume: volume,
  });
}

export function sendVolumeGet() {
  sendWSMessageJson({
    opcode: MessageOpcode.DEVICE_CONFIG,
    action: actionStr,
    pid: getProductId(),
    getVolume: true,
  });
}

export function sendSkipVirtualAssistance() {
  sendWSMessageJson({
    opcode: MessageOpcode.SKIP_VIRTUAL_ASSISTANCE,
    action: actionStr,
    pid: getProductId(),
  });
}

export function sendSkipCalibration() {
  const currentVersion = store.getState().product.currentVersion;
  if (!isVersionEnoughForStartWithCalibrationScene(currentVersion)) {
    console.warn("Skip calibration is not supported for the current headset version.");
    return;
  }

  sendWSMessageJson({
    opcode: MessageOpcode.SKIP_CALIBRATION,
    action: actionStr,
    pid: getProductId(),
  });
}

const extractSnapshotData = (msg: WebSocketMessage): Snapshot => {
  const opCode = parseInt(msg["opcode"]?.toString());
  const snapshot: Snapshot = {
    gazeDataPoint: msg["gazeDataPoint"] ? JSON.parse(msg["gazeDataPoint"]) : undefined,
    headVelocityData: msg["headVelocityData"] ? JSON.parse(msg["headVelocityData"]) : undefined,
    percentagePoint:
      msg["xPercentage"] && msg["yPercentage"]
        ? {
            xPercentage: msg["xPercentage"],
            yPercentage: msg["yPercentage"],
            timeStamp: msg["timeStamp"] ?? msg["timestamp"],
            gazeDelta: msg["gazeDelta"],
            pageIndex: msg["pageIndex"],
          }
        : undefined,
    contrastSensitivityData:
      opCode === MessageOpcode.CONTRAST_TEST_UPDATE
        ? (() => {
            // Basic contrast sensitivity data
            const baseData = {
              grating: msg["grating"] ? msg["grating"] : undefined,
              contrast: msg["contrast"] ? msg["contrast"] : undefined,
            };

            if (baseData.grating !== undefined || baseData.contrast !== undefined) {
              return {
                ...baseData,
                ...(msg["fpFnInfo"] && { fpFnInfo: msg["fpFnInfo"] }),
              };
            }

            // Check if there's Pelli-Robson data in the test field
            if (msg["test"]) {
              try {
                const testData = JSON.parse(msg["test"]);
                if (testData.values && testData.category === "CS") {
                  // This is a Pelli-Robson test
                  return {
                    pelliRobsonLetterSet: testData.values,
                    pelliRobsonIndex: testData.index,
                  };
                }
              } catch (e) {
                console.error("Error parsing test data:", e);
              }
            } else if (msg["index"] !== undefined) {
              return {
                ...baseData,
                pelliRobsonIndex: msg["index"],
              };
            }
            return null;
          })()
        : undefined,
    colorVisionSnapshot:
      opCode === MessageOpcode.COLOR_TEST_UPDATE
        ? (() => {
            const responseObj = msg["response"];
            if (responseObj && typeof responseObj === "object") {
              return {
                plate: responseObj["plate"],
                response: responseObj["response"],
                isCorrect: responseObj["isCorrect"],
                expected: responseObj["expected"],
                responseTimeMs: responseObj["responseTimeMs"],
              };
            }
            if (msg["plate"] !== undefined) {
              return {
                plate: msg["plate"],
                response: msg["response"],
                isCorrect: msg["isCorrect"],
                expected: msg["expected"],
                responseTimeMs: msg["responseTimeMs"],
              };
            }
            return undefined;
          })()
        : undefined,
    colorVisionInitData:
      opCode === MessageOpcode.COLOR_TEST_UPDATE && msg["initialized"] === true
        ? {
            plateCount: msg["plateCount"] ?? 38,
            isOperatorControlled: msg["isOperatorControlled"] ?? true,
            initialized: true,
          }
        : undefined,
    colorVisionResults:
      opCode === MessageOpcode.COLOR_TEST_UPDATE && Array.isArray(msg["results"])
        ? (msg["results"] as any[]).map((r) => ({
            plate: r["plate"] as number,
            response: r["response"] as string | number,
            isCorrect: r["isCorrect"] as boolean,
            expected: r["expected"] as string | number | undefined,
            responseTimeMs: r["responseTimeMs"] as number | undefined,
          }))
        : undefined,
    pupillometryData:
      typeof msg["leftPupil"] === "number" &&
      Number.isFinite(msg["leftPupil"]) &&
      typeof msg["rightPupil"] === "number" &&
      Number.isFinite(msg["rightPupil"]) &&
      typeof msg["timestamp"] === "number" &&
      Number.isFinite(msg["timestamp"]) &&
      msg["stimulusActive"] !== undefined
        ? {
            leftPupil: msg["leftPupil"],
            rightPupil: msg["rightPupil"],
            timestamp: msg["timestamp"],
            stimulusActive: msg["stimulusActive"],
            targetEye: (["Left", "Right", "Both"] as const).includes(msg["targetEye"])
              ? (msg["targetEye"] as "Left" | "Right" | "Both")
              : "Both",
          }
        : undefined,
    ocularMotilityMetrics:
      opCode === MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE && msg["snapshot"] == null
        ? {
            targetVelocity: msg["targetVelocity"],
            rightMeanError: msg["rightMeanError"],
            rightMeanTangentialVelocity: msg["rightMeanTangentialVelocity"],
            rightPursuitGain: msg["rightPursuitGain"],
            rightSaccadeCount: msg["rightSaccadeCount"],
            leftMeanError: msg["leftMeanError"],
            leftMeanTangentialVelocity: msg["leftMeanTangentialVelocity"],
            leftPursuitGain: msg["leftPursuitGain"],
            leftSaccadeCount: msg["leftSaccadeCount"],
          }
        : undefined,
    ocularMotilityData:
      opCode === MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE && msg["snapshot"] == null
        ? {
            leftGazePosition: msg["leftGazePosition"],
            rightGazePosition: msg["rightGazePosition"],
            targetPosition: msg["targetPosition"],
            rightEyeTangentialVelocity: msg["rightEyeTangentialVelocity"],
            leftEyeTangentialVelocity: msg["leftEyeTangentialVelocity"],
            rightEyeError: msg["rightEyeError"],
            leftEyeError: msg["leftEyeError"],
            timestamp: msg["timestamp"],
          }
        : undefined,
    smoothPursuitSnapshot:
      opCode === MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE &&
      msg["snapshot"] != null &&
      msg["snapshot"].trialPhase == null
        ? (msg["snapshot"] as SmoothPursuitSnapshot)
        : undefined,
    antiSaccadeSample: (() => {
      if (opCode !== MessageOpcode.OCULAR_MOTILITY_TEST_UPDATE) return undefined;
      const snap = msg["snapshot"];
      if (snap == null || snap.trialPhase == null) return undefined;
      if (snap.leftGazeAngles == null || snap.rightGazeAngles == null) return undefined;
      if (snap.peripheralHorizontalDegrees == null || snap.mirrorHorizontalDegrees == null)
        return undefined;
      return snap as AntiSaccadeSample;
    })(),
    vAcuityLetterSet: msg["category"] === "VA" ? msg["values"] : undefined,
    vAcuityIndex:
      msg["index"] && opCode === MessageOpcode.VA_TEST_UPDATE ? msg["index"] : undefined,
    tumblingESnapshot:
      msg["snapshot"] && opCode === MessageOpcode.VA_TEST_UPDATE ? msg["snapshot"] : undefined,
    tumblingEThresholdRow:
      opCode === MessageOpcode.VA_TEST_UPDATE && msg["thresholdRow"] !== undefined
        ? (msg["thresholdRow"] as number | null)
        : undefined,
    tumblingEThresholdSnellenSize:
      opCode === MessageOpcode.VA_TEST_UPDATE && msg["thresholdSnellenSize"] !== undefined
        ? (msg["thresholdSnellenSize"] as SnellenSize | null)
        : undefined,
    tumblingEChartStimuli:
      opCode === MessageOpcode.VA_TEST_UPDATE && Array.isArray(msg["chartStimuli"])
        ? (msg["chartStimuli"] as TumblingEObject[])
        : undefined,
    fdtData:
      opCode === MessageOpcode.FDT_TEST_UPDATE
        ? msg["firstButtonPressed"] !== undefined
          ? msg["firstButtonPressed"]
          : msg["label"] !== undefined && msg["index"] !== undefined
            ? [
                {
                  label: msg["label"],
                  value: msg["value"] ?? 0,
                  index: msg["index"],
                  contrast: msg["contrast"],
                },
              ]
            : undefined
        : undefined,
    cptSnapshot: msg["cptSnapshot"],
    coverTestData: msg["coverTestData"],
    stereoAcuityData: msg["stereoAcuityData"],
    kineticStimulus: msg["kineticStimulus"],
    kineticGraphStat: msg["kineticGraphStat"],
    vfaSnapshot: opCode === MessageOpcode.UPDATE_STATUS ? ExtractVFASnapshotData(msg) : undefined,
    dyscalculiaSnapshot: parseDyscalculiaSnapshot(opCode, msg),
    wcstSnapshot:
      opCode === MessageOpcode.WCST_TEST_UPDATE
        ? (() => {
            const raw = msg["snapshot"];
            let trial;
            if (raw != null) {
              const parsed =
                typeof raw === "string"
                  ? (() => {
                      try {
                        return JSON.parse(raw);
                      } catch {
                        return null;
                      }
                    })()
                  : raw;
              if (parsed && typeof parsed.correct === "boolean") {
                trial = {
                  trialIndex: typeof parsed.trialIndex === "number" ? parsed.trialIndex : undefined,
                  correct: parsed.correct,
                  rule: typeof parsed.rule === "string" ? parsed.rule : undefined,
                  reactionTimeMs:
                    typeof parsed.reactionTimeMs === "number" ? parsed.reactionTimeMs : undefined,
                  selectedReferenceCard: parsed.selectedReferenceCard,
                  playerCard: parsed.playerCard,
                  controllerUsed:
                    typeof parsed.controllerUsed === "string" ? parsed.controllerUsed : undefined,
                };
              }
            }
            if (!trial && (msg["correct"] === true || msg["correct"] === false)) {
              trial = {
                trialIndex: typeof msg["trialIndex"] === "number" ? msg["trialIndex"] : undefined,
                correct: msg["correct"] === true,
                rule: typeof msg["rule"] === "string" ? msg["rule"] : undefined,
                reactionTimeMs:
                  typeof msg["reactionTimeMs"] === "number" ? msg["reactionTimeMs"] : undefined,
                selectedReferenceCard: msg["selectedReferenceCard"],
                playerCard: msg["playerCard"],
                controllerUsed:
                  typeof msg["controllerUsed"] === "string" ? msg["controllerUsed"] : undefined,
              };
            }
            if (!trial && (msg["correctness"] === true || msg["correctness"] === false)) {
              const selectionTime =
                typeof msg["selectionTime"] === "number" ? msg["selectionTime"] : undefined;
              trial = {
                trialIndex: typeof msg["trialNumber"] === "number" ? msg["trialNumber"] : undefined,
                correct: msg["correctness"] === true,
                rule: typeof msg["ruleType"] === "string" ? msg["ruleType"] : undefined,
                selectionTime,
                reactionTimeMs:
                  typeof selectionTime === "number" ? Math.round(selectionTime * 1000) : undefined,
                selectedReferenceCard: msg["selectedReferenceCard"],
                playerCard: msg["playerCard"],
                controllerUsed:
                  typeof msg["controllerUsed"] === "string" ? msg["controllerUsed"] : undefined,
              };
            }
            const avgRt =
              typeof msg["averageReactionTimeMs"] === "number"
                ? msg["averageReactionTimeMs"]
                : undefined;
            const avgTrials =
              typeof msg["averageTrialsToFindRule"] === "number"
                ? msg["averageTrialsToFindRule"]
                : undefined;
            if (trial || avgRt !== undefined || avgTrials !== undefined) {
              return { trial, averageReactionTimeMs: avgRt, averageTrialsToFindRule: avgTrials };
            }
            return undefined;
          })()
        : undefined,
    readingAssessmentSnapshot:
      opCode === MessageOpcode.READING_ASSESSMENT_TEST_UPDATE &&
      Array.isArray(msg["comprehensionAnswers"])
        ? {
            comprehensionAnswers: msg[
              "comprehensionAnswers"
            ] as Snapshot["readingAssessmentSnapshot"]["comprehensionAnswers"],
            finish: msg["finish"] === true,
          }
        : undefined,
    npctSnapshot:
      opCode === MessageOpcode.NPCT_TEST_UPDATE && msg["snapshot"] != null
        ? (msg["snapshot"] as NPCTSnapshot)
        : undefined,
    npctUserInput:
      opCode === MessageOpcode.NPCT_TEST_UPDATE && msg["userInput"] != null
        ? (msg["userInput"] as NPCTUserInput)
        : undefined,
    oknSnapshot:
      opCode === MessageOpcode.OKN_TEST_UPDATE && msg["snapshot"] != null
        ? (msg["snapshot"] as OKNProgressSnapshot)
        : undefined,
  };
  return snapshot;
};
